import { Box, styled, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { ThemeTypography } from 'designSystem';
import RiskPieLabel from 'designSystem/DataDisplay/RiskPie/RiskPieLabel';
import React, { FC, useMemo } from 'react';
import { EPlotStatus } from 'types/compliance.types';

interface IGeoDataOverviewChartProps {
  highRisks: number;
  mediumRisks: number;
  lowRisks: number;
  notAnalysed: number;
  inAnalysis: number;
  amountCommodities?: number;
  totalSize?: number;
  amountPartners?: number;
  amountOrigins?: number;
  amountCountries?: number;
}

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  borderRadius: 6,
  border: `1px solid ${theme.custom.colors.lightBorderColor}`,
  background: theme.custom.themeColors.white,
  padding: theme.spacing(2),
}));

const PlotStatusBadge = styled(Box)<{ color: string }>(({ theme, color }) => ({
  position: 'relative',
  backgroundColor: theme.custom.themeColors.primary[5],
  borderRadius: 20,
  padding: theme.spacing(0.5, 2, 0.5, 3.5),
  display: 'flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    display: 'block',
    width: '13px',
    height: '13px',
    borderRadius: '50%',
    backgroundColor: color,
    position: 'absolute',
    top: '50%',
    left: theme.spacing(2),
    transform: 'translate(-50%, -50%)',
  },
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  width: 'fit-content',
  '& .MuiPieArc-root': {
    strokeWidth: 0,
  },
}));

const NumberContainer = styled(Box)(({ theme }) => ({
  background: theme.custom.themeColors.grayScale[10],
  borderRadius: 4,
  padding: theme.spacing(0.5, 1),
}));

const GeoDataOverviewChart: FC<IGeoDataOverviewChartProps> = ({
  highRisks,
  mediumRisks,
  lowRisks,
  inAnalysis,
  notAnalysed,
  amountCommodities,
  totalSize,
  amountPartners,
  amountOrigins,
  amountCountries,
}) => {
  const { custom } = useTheme();

  const totalPlots = lowRisks + mediumRisks + highRisks + inAnalysis + notAnalysed;

  const getPercentage = (value: number) =>
    totalPlots === 0 ? '0' : ((value / totalPlots) * 100).toFixed(0);

  // data for pie chart sorted by amount
  const sortedData = useMemo(
    () =>
      [
        {
          label: 'Low risk',
          value: lowRisks,
          color: custom.themeColors.success[100],
          percentage: getPercentage(lowRisks),
          plotStatus: EPlotStatus.LOW,
        },
        {
          label: 'Medium risk',
          value: mediumRisks,
          color: custom.themeColors.accent[100],
          percentage: getPercentage(mediumRisks),
          plotStatus: EPlotStatus.MEDIUM,
        },
        {
          label: 'High risk',
          value: highRisks,
          color: custom.themeColors.error[80],
          percentage: getPercentage(highRisks),
          plotStatus: EPlotStatus.HIGH,
        },
        {
          label: 'In analysis',
          value: inAnalysis,
          color: custom.colors.lightViolet,
          percentage: getPercentage(inAnalysis),
          plotStatus: EPlotStatus.IN_ANALYSIS,
        },
        {
          label: 'Not analysed',
          value: notAnalysed,
          color: custom.themeColors.primary[40],
          percentage: getPercentage(notAnalysed),
          plotStatus: EPlotStatus.NOT_ANALYSED,
        },
      ].sort((a, b) => b.value - a.value),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lowRisks, totalPlots, mediumRisks, highRisks, inAnalysis, notAnalysed]
  );

  return (
    <Container>
      <ChartContainer>
        <PieChart
          series={[
            {
              data:
                totalPlots === 0
                  ? [
                      {
                        value: 1,
                        color: custom.themeColors.grayScale[20],
                      },
                    ]
                  : sortedData,
              innerRadius: 120 / 3.5,
            },
          ]}
          margin={{ right: 0, left: 0, top: 0, bottom: 0 }}
          height={120}
          width={120}
          slotProps={{
            legend: { hidden: true },
          }}
          tooltip={{ trigger: 'none' }}
        >
          <RiskPieLabel
            label={`${sortedData[0].percentage}%`}
            subLabel={sortedData[0].label}
            fontSizeLabel={16}
            fontSizeSublabel={8}
          />
        </PieChart>
      </ChartContainer>
      <Box display="flex" flexDirection="column" gap={2} width="100%">
        <ThemeTypography variant="BODY_LARGE_BOLD">
          {`${totalPlots} plots under due diligence`}
        </ThemeTypography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {!!totalSize && (
            <NumberContainer>
              <ThemeTypography variant="BODY_SMALL_BOLD">{`${totalSize.toFixed(
                2
              )} ha`}</ThemeTypography>
            </NumberContainer>
          )}
          {!!amountCommodities && (
            <NumberContainer>
              <ThemeTypography variant="BODY_SMALL_BOLD">{`${amountCommodities} commodities`}</ThemeTypography>
            </NumberContainer>
          )}
          {!!amountPartners && (
            <NumberContainer>
              <ThemeTypography variant="BODY_SMALL_BOLD">{`${amountPartners} suppliers`}</ThemeTypography>
            </NumberContainer>
          )}
          {!!amountOrigins && (
            <NumberContainer>
              <ThemeTypography variant="BODY_SMALL_BOLD">{`${amountOrigins} origins ${
                amountCountries && `(${amountOrigins} countries)`
              }`}</ThemeTypography>
            </NumberContainer>
          )}
          {!!amountCountries && !amountOrigins && (
            <NumberContainer>
              <ThemeTypography variant="BODY_SMALL_BOLD">{`${amountCountries} countries`}</ThemeTypography>
            </NumberContainer>
          )}
        </Box>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {sortedData.map(({ label, percentage, color }) => (
            <PlotStatusBadge key={label} color={color}>
              <ThemeTypography variant="BODY_SMALL">{`${percentage}% ${label}`}</ThemeTypography>
            </PlotStatusBadge>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default GeoDataOverviewChart;
